import { Link, useNavigate } from 'react-router-dom';
import { isPublicationOpen, type Org, type Reel } from '@reelmachine/core';
import { selectOrg, useCurrentOrg, useReels } from '../api/hooks';
import { useAuth } from '../auth/AuthProvider';
import { formatCount } from '../lib/format';
import { Icon } from '../ui/Icon';
import { ErrorView, Loading, Screen } from '../ui/Screen';
import { stagePath } from '../ui/Stepper';

export function brandColor(org: Org | undefined): string {
  return org?.brand?.theme.main ?? '#1B6F9B';
}

function StatusChip({ reel }: { reel: Reel }) {
  if (reel.job?.status === 'running' && reel.job.kind === 'script')
    return <span className="chip rec">Writing script</span>;
  if (reel.job?.status === 'failed') return <span className="chip warn">Needs attention</span>;
  if (isPublicationOpen(reel.publication)) return <span className="chip rec">Posting</span>;
  if (reel.publication?.state === 'rejected' && reel.stage !== 'stats')
    return <span className="chip warn">Not posted</span>;
  if (reel.stage === 'stats') {
    return (
      <>
        <span className="chip ok">Live</span>
        {reel.metrics && <span className="nums">{formatCount(reel.metrics.views)} views</span>}
      </>
    );
  }
  const label = { script: 'Script ready', record: 'Record', video: 'Video', post: 'Post' }[reel.stage];
  return <span className={`chip ${reel.stage === 'script' ? 'rec' : ''}`}>{label}</span>;
}

function ReelRow({ reel, brand }: { reel: Reel; brand: string }) {
  return (
    <Link className="reel" to={stagePath(reel.id, reel.stage)}>
      <div className="thumb" aria-hidden="true">
        <i style={{ background: `linear-gradient(${brand}, transparent)` }} />
        <span>{reel.concept?.hook ?? reel.title}</span>
      </div>
      <div className="meta">
        <h3>{reel.title}</h3>
        <div className="row" style={{ gap: 8 }}>
          <StatusChip reel={reel} />
        </div>
      </div>
    </Link>
  );
}

export function ReelList() {
  const navigate = useNavigate();
  const auth = useAuth();
  const { org, orgId, me } = useCurrentOrg();
  const reels = useReels(orgId);

  if (me.isError) return <ErrorView error={me.error} />;
  if (me.isPending) return <Loading />;
  if (!orgId) {
    return (
      <Screen title="Reel Machine">
        <div className="card warnc" role="alert">
          <h3>No workspace yet</h3>
          <p>Your account is not part of a team. Ask an admin to add you.</p>
        </div>
      </Screen>
    );
  }
  if (reels.isError) return <ErrorView error={reels.error} />;
  if (reels.isPending) return <Loading />;

  const connected = org?.zernio?.status === 'connected';
  const brand = brandColor(org);
  return (
    <Screen
      title="Reel Machine"
      width="wide"
      right={
        <>
          {me.data.orgs.length > 1 && (
            <select
              className="orgsel"
              aria-label="Organisation"
              value={orgId}
              onChange={(e) => selectOrg(e.target.value)}
            >
              {me.data.orgs.map((o) => (
                <option key={o.id} value={o.id}>
                  {o.name}
                </option>
              ))}
            </select>
          )}
          {me.data.staff && (
            <Link className="iconbtn" to="/learning" aria-label="Learning">
              <Icon name="lightbulb" />
            </Link>
          )}
          <Link
            className="iconbtn"
            to="/channel"
            aria-label={org?.instagramHandle ? `Channel @${org.instagramHandle}` : 'Channel setup'}
            title={org?.instagramHandle ? `@${org.instagramHandle}` : 'Channel setup'}
          >
            {org?.avatarUrl ? <img className="avatar" src={org.avatarUrl} alt="" /> : <Icon name="instagram" />}
          </Link>
          {!auth.mock && (
            <button type="button" className="iconbtn" aria-label="Sign out" onClick={() => auth.signOut()}>
              <Icon name="logout" />
            </button>
          )}
        </>
      }
      bar={
        <button type="button" className="btn" onClick={() => navigate('/new')}>
          <Icon name="plus" /> New reel
        </button>
      }
    >
      {!connected && (
        <button type="button" className="card warnc" onClick={() => navigate('/channel')}>
          <h3>Instagram not connected</h3>
          <p>Connect it in setup to post.</p>
        </button>
      )}
      <span className="label" id="reels-label">
        Your reels
      </span>
      {reels.data.length === 0 ? (
        <p className="muted">No reels yet. Start with a press release or an article.</p>
      ) : (
        <ul aria-labelledby="reels-label" className="reel-grid">
          {reels.data.map((r) => (
            <li key={r.id}>
              <ReelRow reel={r} brand={brand} />
            </li>
          ))}
        </ul>
      )}
    </Screen>
  );
}
